import { useState } from 'react';
import { add, EntityType, EntityTypeSchema } from '@/api/doc/entity-type';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddEntityType() {
	const [isSheetOpen, setIsSheetOpen] = useState(false);
	const queryClient = useQueryClient();

	const form = useForm<EntityType>({
		resolver: zodResolver(EntityTypeSchema),
		defaultValues: {
			id: null,
			name: '',
			slug: '',
			deletedOn: null,
		},
		mode: 'all',
	});

	const { mutate } = useMutation({
		mutationFn: (entityType: EntityType) => add(entityType),
		onSuccess: () => {
			toast.success(`Success! The entity type has been successfully added`, {
				dismissible: true,
			});
			setIsSheetOpen(false);
			queryClient.invalidateQueries({ queryKey: ['entity-types'] });
		},
		onError: () => {
			toast.error(`Error! The entity type could not be added`, {
				dismissible: true,
			});
		},
	});
	const onSubmit: SubmitHandler<EntityType> = (values: EntityType) =>
		mutate(values);

	form.watch();

	return (
		<Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
			<SheetTrigger asChild>
				<Button>
					<CirclePlus />
					Add Entity Type
				</Button>
			</SheetTrigger>
			<SheetContent className="overflow-scroll">
				<SheetHeader>
					<SheetTitle>Add Entity Type</SheetTitle>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Entity Type Name</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Entity type name" />
									</FormControl>
									<FormDescription>Name the entity type</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							disabled={!form.formState.isValid || form.formState.isSubmitting}
						>
							{form.formState.isSubmitting ? 'Adding...' : 'Add'}
						</Button>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
}
